import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { TreeSelect } from 'antd';

import type { FieldControlProps } from '../../../components/ui/Field';
import { useT } from '../../../i18n';
import { DO_SAU_TOI_DA, cayLuaChon, chuanHoaTim, toTienCua } from '../nghiep-vu/cay';
import type { NutLuaChon } from '../nghiep-vu/cay';
import type { Department } from '../types';

/** Nút đưa cho `TreeSelect`: `title` vẽ trong cây, `nhan` hiện trong ô đã chọn. */
interface NutHien {
  value: string;
  key: string;
  ten: string;
  title: ReactNode;
  nhan: ReactNode;
  disabled: boolean;
  children?: NutHien[];
}

/**
 * Ô chọn phòng ban dạng cây (`TreeSelect` của antd): thụt lề theo cấp, có nút
 * mở/gập nhánh, mở sẵn nhánh chứa giá trị đang chọn. Ô tìm không phân biệt
 * dấu, giữ nhánh cha của kết quả để thấy phòng ban nằm ở đâu.
 *
 * Bỏ trống (nút xoá của ô) là "không có phòng ban" — nghĩa cụ thể do
 * `placeholder` của phía gọi nói. Ô đã chọn hiện tên phòng ban, tên đầy đủ từ
 * gốc ở tooltip.
 *
 * - `dangSua`: bỏ phòng ban này và con cháu của nó (chọn cha cho chính nó).
 * - `khoaQuaSau`: khoá, kèm lý do, phòng ban mà đặt vào thì cây vượt
 *   `DO_SAU_TOI_DA` cấp — chỉ có nghĩa khi chọn phòng ban cha.
 */
export function CayChonPhongBan({
  fieldProps,
  ds,
  dangSua,
  khoaQuaSau = false,
  value,
  onChange,
  placeholder,
  khongCo,
  loading,
  disabled,
}: {
  fieldProps: FieldControlProps;
  ds: readonly Department[];
  dangSua?: string;
  khoaQuaSau?: boolean;
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder: string;
  /** Chữ khi ô tìm không ra phòng ban nào. */
  khongCo: string;
  loading?: boolean;
  disabled?: boolean;
}) {
  const { t, locale } = useT();
  const lyDoQuaSau = t('coCauToChuc.fChaQuaSau', { so: DO_SAU_TOI_DA });

  const treeData = useMemo(() => {
    const doi = (ds2: readonly NutLuaChon[]): NutHien[] =>
      ds2.map((n) => {
        const khoa = khoaQuaSau && n.quaSau;
        return {
          value: n.value,
          key: n.value,
          ten: n.ten,
          disabled: khoa,
          title: khoa ? (
            <span className="inline-flex min-w-0 items-baseline gap-2" title={lyDoQuaSau}>
              <span className="truncate">{n.ten}</span>
              <span className="shrink-0 text-2xs text-content-muted">
                {t('coCauToChuc.fChaQuaSauNgan', { so: DO_SAU_TOI_DA })}
              </span>
            </span>
          ) : (
            <span title={n.duong}>{n.ten}</span>
          ),
          nhan: <span title={n.duong}>{n.ten}</span>,
          ...(n.children ? { children: doi(n.children) } : {}),
        };
      });
    return doi(cayLuaChon(ds, dangSua, locale));
  }, [ds, dangSua, locale, khoaQuaSau, lyDoQuaSau, t]);

  // Mở sẵn nhánh chứa giá trị đang chọn; chỉ đọc lúc dựng ô.
  const moSan = useMemo(() => toTienCua(ds, value), [ds, value]);

  return (
    <TreeSelect<string>
      {...fieldProps}
      className="w-full"
      treeData={treeData}
      treeLine
      treeNodeLabelProp="nhan"
      treeDefaultExpandedKeys={moSan}
      showSearch={{
        filterTreeNode: (input, node) => chuanHoaTim(String((node as unknown as NutHien).ten)).includes(chuanHoaTim(input)),
      }}
      allowClear
      loading={loading}
      disabled={disabled}
      placeholder={placeholder}
      notFoundContent={khongCo}
      value={value ?? undefined}
      onChange={(v) => onChange(v ?? null)}
    />
  );
}
